import * as React from "react";
import { Node as _Node, Handle, Position } from "reactflow";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripVertical } from "@fortawesome/free-solid-svg-icons";
import { ORPHAN_PURPLE } from "../decision-tree-colors";

export namespace CustomNode {
    export const Type = "decisionTreeNode";

    export interface Data {
        overviewText: string;
        isLeaf: boolean;
        isRoot: boolean;
        useCustomizedLayout?: boolean;
        isOrphan?: boolean;
        /**
         * @deprecated Background color is now set via ReactFlow node.style in the factory.
         * Kept for backward compatibility but no longer read by this component.
         */
        backgroundColor?: string;
    }

    export type Node = _Node<Data>;

    const defaultTextStyle: React.CSSProperties = {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxHeight: "40px",
        lineHeight: "20px",
        fontSize: "12px"
    };

    const unlinkedBadgeStyle: React.CSSProperties = {
        display: "inline-block",
        background: ORPHAN_PURPLE,
        color: "white",
        fontSize: "9px",
        fontWeight: "bold",
        padding: "1px 6px",
        borderRadius: "8px",
        marginBottom: "2px",
        letterSpacing: "0.5px"
    };

    /**
     * CustomNode component for ReactFlow decision tree nodes.
     *
     * Node Inspector ("magnifying glass") implementation:
     * - On hover, broadcasts the node's data to a shared inspector panel via a
     *   CustomEvent dispatched on `window`. The inspector panel (rendered in
     *   ReactFlowCanvas) listens for this event and displays the full overview text.
     * - This approach avoids per-node tooltip DOM elements (N tooltips for N nodes)
     *   and keeps the inspector at a fixed screen position (bottom-right corner)
     *   so the user's eye doesn't need to chase a moving tooltip.
     * - 300ms hover delay prevents the inspector from flickering as the mouse
     *   moves across nodes quickly.
     */
    /** Props include ReactFlow's `id` for the node, plus our custom `data`. */
    export const Comp = (props: { id: string; data: Data }) => {
        const hoverTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
        const nodeId = props.id;
        // Ref to always read latest props.data inside the delayed timeout
        const dataRef = React.useRef(props.data);
        dataRef.current = props.data;

        const handleMouseEnter = React.useCallback(() => {
            // 300ms delay: prevents inspector flicker when moving mouse across nodes quickly
            hoverTimer.current = setTimeout(() => {
                const d = dataRef.current; // Always latest data at fire time
                window.dispatchEvent(new CustomEvent("node-inspector", {
                    detail: { nodeId, overviewText: d.overviewText, isRoot: d.isRoot, isLeaf: d.isLeaf, isOrphan: d.isOrphan }
                }));
            }, 300);
        }, [nodeId]);

        const handleMouseLeave = React.useCallback(() => {
            if (hoverTimer.current) {
                clearTimeout(hoverTimer.current);
                hoverTimer.current = null;
            }
            // Include nodeId so the inspector only dismisses if this node is active
            window.dispatchEvent(new CustomEvent("node-inspector", { detail: { dismiss: nodeId } }));
        }, [nodeId]);

        // Cleanup on unmount: clear pending timer AND request dismiss for this node only.
        // The inspector ignores the dismiss if a different node is currently displayed,
        // preventing an unrelated node's unmount from blanking the panel.
        React.useEffect(() => {
            return () => {
                if (hoverTimer.current) clearTimeout(hoverTimer.current);
                window.dispatchEvent(new CustomEvent("node-inspector", { detail: { dismiss: nodeId } }));
            };
        }, [nodeId]);

        return (
            <div
                style={{ overflow: "hidden" }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {/* Target handle: shown for non-root nodes AND orphan nodes */}
                {(!props.data.isRoot || props.data.isOrphan) && (
                    <Handle type="target" position={Position.Top} id="t" draggable={false} />
                )}
                {props.data.isOrphan && (
                    <div style={{ textAlign: "center" }}>
                        <span style={unlinkedBadgeStyle}>UNLINKED</span>
                    </div>
                )}
                {props.data.useCustomizedLayout && (
                    <div
                        className="node-drag-handle"
                        style={{
                            cursor: "move",
                            textAlign: "center",
                            fontSize: "10px",
                            color: "rgba(0,0,0,0.3)",
                            marginBottom: "2px"
                        }}
                    >
                        <FontAwesomeIcon icon={faGripVertical} aria-label="Drag to reposition" />
                    </div>
                )}
                <div style={defaultTextStyle}>
                    {props.data.overviewText}
                </div>
                <Handle type="source" position={Position.Bottom} id="b" draggable={false} />
            </div>
        );
    };

    export const List = { [Type]: CustomNode.Comp };
}
